import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";

import { FaLinkedinIn } from "react-icons/fa";
import { MdOutlineEmail } from "react-icons/md";
import { FaPhone, FaLocationDot, FaBell } from "react-icons/fa6";

import { StudentAction, NotificationsAction } from "../../store/actions";

import {
  Loader,
  tabs,
  Spacer,
  Buttons,
  inputFields,
  Modal,
} from "../../components";

import { Images, UtilityArrays, Utils } from "../../config";

import StudentGeneral from "./student-general";
import StudentApplications from "./student-applications";
import StudentMeetings from "./student-meetings";
import StudentProfileBuilder from "./student-profilebuilder";

import "./styles.css";

const StudentDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const id = location.pathname.split("/")[3];

  const profileData = useSelector((state) => state?.profile?.user);
  const { student, isStudentLoading } = useSelector((state) => state?.students);
  const schoolId =
    profileData?.role === "school"
      ? profileData?.schoolId
      : profileData?.staff?.schoolId;

  const tab = ["general", "applications", "meetings", "profileBuilder"];
  const tabName = location.pathname.split("/")[2];
  const tabKey = String(tab.indexOf(tabName) + 1);

  const [notificationModal, setNotificationModal] = useState(false);
  const [notification, setNotification] = useState({
    text: "",
    title: "",
  });

  useEffect(() => {
    dispatch(StudentAction.getStudentDetails({ userId: id }));
  }, [profileData]);

  const handleUpdate = (userId, data) => {
    const payload = { userId, data, schoolId };
    dispatch(
      StudentAction.updateStudent(payload, () =>
        dispatch(StudentAction.getStudentDetails({ userId: id }))
      )
    );
  };

  const handleNotify = () => {
    if (!notification?.title || !notification?.text) {
      message.error("Please fill all the fields");
      return;
    } else {
      const payload = { ...notification, user: id };
      dispatch(NotificationsAction.addNotificationsSingle(payload));
      setNotificationModal(false);
      setNotification({ title: "", text: "" });
    }
  };

  const tabsArray = [
    {
      label: (
        <div className="uni-details-tabs-container">
          <div className="uni-details-tabs-image">
            <img src={Images.Unigeneral} />
          </div>
          <div>General</div>
        </div>
      ),
      key: "1",
      children: <StudentGeneral studentdata={student?.general} />,
    },
    {
      label: (
        <div className="uni-details-tabs-container">
          <div className="uni-details-tabs-image">
            <img src={Images.StudentApplications} />
          </div>
          <div>Applications</div>
        </div>
      ),
      key: "2",
      children: <StudentApplications studentdata={student?.applications} />,
    },
    {
      label: (
        <div className="uni-details-tabs-container">
          <div className="uni-details-tabs-image">
            <img src={Images.StudentMeetings} />
          </div>
          <div>Meetings</div>
        </div>
      ),
      key: "3",
      children: <StudentMeetings studentdata={student?.meetings} />,
    },
    {
      label: (
        <div className="uni-details-tabs-container">
          <div className="uni-details-tabs-image">
            <img src={Images.StudentProfileBuilder} />
          </div>
          <div>Profile Builder</div>
        </div>
      ),
      key: "4",
      children: <StudentProfileBuilder studentdata={student?.profileBuilder} />,
    },
  ];

  const heroRightData = [
    {
      icon: <FaLinkedinIn color="black" fontSize={"0.75em"} />,
      value: student?.common?.student?.linkedIn ? (
        <Link to={student?.common?.student?.linkedIn ?? ""} target="_blank">
          <div className="text-white font-normal text-[0.875em] underline">
            {student?.common?.student?.linkedIn ?? "NA"}
          </div>
        </Link>
      ) : (
        <div>
          <div className="text-white font-normal text-[0.875em]">
            {student?.common?.student?.linkedIn ?? "NA"}
          </div>
        </div>
      ),
    },
    {
      icon: <MdOutlineEmail color="black" fontSize={"0.75em"} />,
      value: (
        <Link to={`mailto:${student?.common?.email}`}>
          <div className="text-white font-normal text-[0.875em]">
            {student?.common?.email}
          </div>
        </Link>
      ),
    },
    {
      icon: <FaPhone color="black" fontSize={"0.625em"} />,
      value: (
        <div>
          {student?.common?.phoneNumber?.trim()?.startsWith("+")
            ? student?.common?.phoneNumber
            : `+${student?.common?.phoneNumber}`}
        </div>
      ),
    },
    {
      icon: <FaLocationDot color="black" fontSize={"0.625em"} />,
      value: <div>{student?.common?.country ?? "NA"}</div>,
    },
  ];

  return (
    <div className="text-black">
      {isStudentLoading ? (
        <Loader.Circular />
      ) : (
        <>
          <div
            className="university-hero-section"
            style={{
              backgroundImage: `url(${Images?.StudentBanner})`,
            }}
          >
            <div className="flex justify-between">
              <div></div>
              <div className="hero-right-part">
                <div className="pb-2 border-b">
                  <div>Contact Details</div>
                </div>
                {heroRightData?.map((item, index) => (
                  <div
                    className="flex gap-2 items-center text-[0.875em]"
                    key={index}
                  >
                    <div className="bg-white rounded-full flex justify-center items-center p-1">
                      {item.icon}
                    </div>
                    <div className="pt-1 overflow-hidden text-dot">
                      {item?.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between gap-2 px-5 pt-2 pb-4 bg-gradient-to-tl from-[#000000c7] from-1% to-transparent to-90% rounded-b-2xl">
              <div className="flex gap-3 text-white items-center">
                <img
                  src={student?.common?.profileImage}
                  className="uni-profilepic-container"
                />
                <div className="flex flex-col gap-2 ">
                  <div className="text-[1.375em] uppercase font-bold">
                    {student?.common?.firstName} {student?.common?.lastName}
                  </div>

                  <div className="font-semibold">
                    {student?.common?.student?.gender &&
                      `${student?.common?.student?.gender} | `}
                    {student?.common?.student?.age &&
                      `${student?.common?.student?.age} Years | `}
                    {student?.common?.student?.grade &&
                      `${student?.common?.student?.grade}`}
                  </div>
                </div>
              </div>
              <div className="flex gap-3 items-center justify-end flex-wrap w-[60%]">
                <div className="w-[12.5em]">
                  <inputFields.SelectDropdown
                    items={Utils.filterArray(UtilityArrays.gradeArray)}
                    value={student?.common?.student?.grade}
                    onChange={(e) =>
                      handleUpdate(student?.common?.id, {
                        student: { update: { grade: e } },
                      })
                    }
                  />
                </div>

                <Buttons.WhiteButtonWithoutBorder
                  style={{ padding: "0.625em 2.25em" }}
                  onClick={() => setNotificationModal(true)}
                >
                  <div className="flex gap-2 overflow-hidden text-ellipsis items-center">
                    <FaBell className="text-pendingText text-[1em]" /> Send
                    Notification
                  </div>
                </Buttons.WhiteButtonWithoutBorder>

                <Buttons.WhiteButtonWithoutBorder
                  style={{ padding: "0.625em 2.25em" }}
                >
                  Add Task
                </Buttons.WhiteButtonWithoutBorder>
                <Buttons.DefaultButton
                  style={{ padding: "0.625em 2.25em" }}
                  onClick={() => {
                    navigate("/meetings/add-meeting", {
                      state: { studentUserId: student?.common?.id },
                    });
                  }}
                >
                  Request Meeting
                </Buttons.DefaultButton>
              </div>
            </div>
          </div>
          <Spacer height={30} />
          <tabs.UniversityTab
            key={tabKey}
            defaultActiveKey={tabKey}
            items={tabsArray}
            onChange={(value) => {
              switch (value) {
                case "1":
                  navigate(`/students/general/${id}`);
                  break;
                case "2":
                  navigate(`/students/applications/${id}`);
                  break;
                case "3":
                  navigate(`/students/meetings/${id}`);
                  break;
                case "4":
                  navigate(`/students/profileBuilder/${id}`);
                  break;
                default:
                  navigate(`/students/general/${id}`);
              }
            }}
          />
        </>
      )}
      <Modal.DefaultModal
        open={notificationModal}
        onCancel={() => setNotificationModal(false)}
      >
        <div className="flex flex-col items-center justify-center gap-4 px-5 py-4 text-center text-[1.125em] w-[37.5em]">
          <div className="text-main text-[1.5em] font-semibold">
            Notification For Student
          </div>

          <div className="flex flex-col gap-4 w-full">
            <div className="flex flex-col justify-left items-start gap-2">
              <div className="text-[1.125em] font-semibold pl-2">
                Notification Title
              </div>
              <inputFields.DefaultInput
                value={notification?.title}
                placeholder="Enter title"
                onChange={(e) =>
                  setNotification({ ...notification, title: e.target.value })
                }
              />
            </div>
            <div className="flex flex-col justify-left items-start gap-2">
              <div className="text-[1.125em] font-semibold pl-2">
                Notification Description
              </div>
              <inputFields.DefaultTextbox
                value={notification?.text}
                placeholder="Enter description"
                onChange={(e) =>
                  setNotification({ ...notification, text: e.target.value })
                }
              />
            </div>
          </div>
          <Spacer height={10} />

          <Buttons.DefaultButton onClick={handleNotify}>
            Send Notification
          </Buttons.DefaultButton>
        </div>
      </Modal.DefaultModal>
    </div>
  );
};

export default StudentDetails;
